import React from 'react';

import { defineMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import LozengeIcon from '@atlaskit/icon-lab/core/lozenge';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { Box, Flex, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { type StatusCategory, type UpdateStatusRovoPayload } from '@atlaskit/rovo-triggers/types';
import SectionMessage from '@atlaskit/section-message/message';
import SectionMessageAction from '@atlaskit/section-message/message-action';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { UpdateStatusActionSkeleton } from './UpdateStatusActionSkeleton';
import { useStatusWorkflowUsages } from './useStatusWorkflowUsages';

const updateStatusNameHeadingMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.updateStatusNameHeading',
	defaultMessage: 'Update status name',
	description:
		'Heading for the section of a Jira workflow builder action card that shows the new name a status will be renamed to.',
});

const statusCategoryHeadingMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryHeading',
	defaultMessage: 'Status category',
	description:
		'Heading for the section of a Jira workflow builder action card that shows the category a status will be moved to.',
});

const statusCategoryTodoMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryTodo',
	defaultMessage: 'To do',
	description: 'Name of the Jira status category for work that has not been started.',
});

const statusCategoryInProgressMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryInProgress',
	defaultMessage: 'In progress',
	description: 'Name of the Jira status category for work that is underway.',
});

const statusCategoryDoneMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryDone',
	defaultMessage: 'Done',
	description: 'Name of the Jira status category for work that is complete.',
});

const statusCategoryUndefinedMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.statusCategoryUndefined',
	defaultMessage: 'None',
	description: 'Shown when a Jira status has no category assigned.',
});

const multiWorkflowUsageWarningMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.multiWorkflowUsageWarning',
	defaultMessage:
		'Changes will impact multiple workflows that reference this status, and may also affect filters and reports. Alternatively, you can replace status instead.',
	description:
		'Warning shown on a Jira workflow builder action card when the status being edited is also used by other workflows.',
});

const workflowUsageCheckInProgressMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.workflowUsageCheckInProgress',
	defaultMessage: 'Checking whether other workflows use this status.',
	description:
		'Announced to screen reader users while a Jira workflow builder action card checks whether the status being edited is used by other workflows.',
});

const unknownWorkflowUsageWarningMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.unknownWorkflowUsageWarning',
	defaultMessage:
		'Failed to load associated workflows. Changes may impact multiple workflows that reference this status, and may also affect filters and reports.',
	description:
		'Warning shown on a Jira workflow builder action card when the check for other workflows using the status being edited could not be completed.',
});

const viewWorkflowsActionMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.viewWorkflowsAction',
	defaultMessage: 'View workflows',
	description:
		'Link on a Jira workflow builder action card that opens the admin page listing every status and the workflows using it.',
});

const replaceStatusActionMessage = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.jira-workflow-wizard-actions.UpdateStatusAction.replaceStatusAction',
	defaultMessage: 'Replace status',
	description:
		'Button on a Jira workflow builder action card that swaps the status being edited for a different one, instead of changing it everywhere it is used.',
});

const VIEW_WORKFLOWS_URL = '/secure/admin/ViewStatuses.jspa';

/**
 * TODO: HIX-9892 implements open the Replace Status modal in WFE
 */
const onReplaceStatusClick = () => {};

const STATUS_CATEGORY_TO_LOZENGE_APPEARANCE = {
	UNDEFINED: 'neutral',
	TODO: 'neutral',
	DONE: 'success',
	IN_PROGRESS: 'information',
} as const;

const STATUS_CATEGORY_TO_LABEL = {
	UNDEFINED: statusCategoryUndefinedMessage,
	TODO: statusCategoryTodoMessage,
	DONE: statusCategoryDoneMessage,
	IN_PROGRESS: statusCategoryInProgressMessage,
} as const;

const styles = cssMap({
	statusIcon: {
		width: '24px',
		height: '24px',
		backgroundColor: token('color.background.accent.gray.subtlest'),
		borderRadius: token('radius.small'),
		color: token('color.text.accent.gray'),
		flexShrink: 0,
	},
	card: {
		width: '100%',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		backgroundColor: token('elevation.surface.raised'),
	},
	swatch: {
		width: '16px',
		height: '16px',
		borderRadius: token('radius.small'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		flexShrink: 0,
	},
	UNDEFINED: {
		borderColor: token('color.border.bold'),
		backgroundColor: token('color.background.neutral'),
	},
	TODO: {
		borderColor: token('color.border.bold'),
		backgroundColor: token('color.background.neutral'),
	},
	IN_PROGRESS: {
		borderColor: token('color.border.brand'),
		backgroundColor: token('color.background.selected'),
	},
	DONE: {
		borderColor: token('color.border.success'),
		backgroundColor: token('color.background.success'),
	},
});

export const UpdateStatusAction = ({
	cloudId,
	statusId,
	fromStatusCategory,
	toStatusName,
	toStatusCategory,
}: UpdateStatusRovoPayload & { cloudId: string }): React.JSX.Element | null => {
	di(useStatusWorkflowUsages);

	const { formatMessage } = useIntl();
	const isStatusEdited = Boolean(toStatusName || toStatusCategory);

	const { isUsedInOtherWorkflows, hasError, isFetching } = useStatusWorkflowUsages({
		cloudId,
		statusId,
		enabled: isStatusEdited,
	});

	if (!isStatusEdited) {
		return null;
	}

	const shouldWarn = hasError || isUsedInOtherWorkflows;

	const lozengeCategory: StatusCategory = toStatusCategory ?? fromStatusCategory ?? 'UNDEFINED';

	return (
		<Stack space="space.100">
			{(isFetching || shouldWarn) && (
				<Box role="status">
					{isFetching ? (
						<>
							<VisuallyHidden>{formatMessage(workflowUsageCheckInProgressMessage)}</VisuallyHidden>
							<UpdateStatusActionSkeleton
								hasStatusName={Boolean(toStatusName)}
								hasStatusCategory={Boolean(toStatusCategory)}
							/>
						</>
					) : (
						<SectionMessage
							appearance="warning"
							actions={[
								<SectionMessageAction
									key="view-workflows"
									href={VIEW_WORKFLOWS_URL}
									target="_blank"
								>
									{formatMessage(viewWorkflowsActionMessage)}
								</SectionMessageAction>,
								<SectionMessageAction key="replace-status" onClick={onReplaceStatusClick}>
									{formatMessage(replaceStatusActionMessage)}
								</SectionMessageAction>,
							]}
						>
							<Text as="p">
								{formatMessage(
									hasError ? unknownWorkflowUsageWarningMessage : multiWorkflowUsageWarningMessage,
								)}
							</Text>
						</SectionMessage>
					)}
				</Box>
			)}

			{!isFetching && (
				<>
					{toStatusName && (
						<Stack space="space.100">
							<Heading size="xsmall">{formatMessage(updateStatusNameHeadingMessage)}</Heading>
							<Inline xcss={styles.card} alignBlock="center" space="space.150">
								<Flex xcss={styles.statusIcon} justifyContent="center" alignItems="center">
									<LozengeIcon label="" />
								</Flex>
								<Lozenge appearance={STATUS_CATEGORY_TO_LOZENGE_APPEARANCE[lozengeCategory]}>
									{toStatusName}
								</Lozenge>
							</Inline>
						</Stack>
					)}

					{toStatusCategory && (
						<Stack space="space.100">
							<Heading size="xsmall">{formatMessage(statusCategoryHeadingMessage)}</Heading>
							<Inline xcss={styles.card} alignBlock="center" space="space.150">
								<Box xcss={cx(styles.swatch, styles[toStatusCategory])} />
								<Text>{formatMessage(STATUS_CATEGORY_TO_LABEL[toStatusCategory])}</Text>
							</Inline>
						</Stack>
					)}
				</>
			)}
		</Stack>
	);
};
